// src/ServiceAreaManagement.js
import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/apiUtils';
import { 
  Container, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

// Styling constants
const styles = {
  tableContainer: {
    marginTop: '1rem',
    overflowX: 'auto',
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
  },
  editableCell: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  editingCell: {
    padding: '0',
  },
  actionCell: {
    whiteSpace: 'nowrap',
    width: '100px',
  },
  listViewHeader: {
    marginTop: '1rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  newRowButton: {
    margin: '1rem 0',
  }
};

const ServiceAreaManagement = () => {
  // State for service areas
  const [serviceAreas, setServiceAreas] = useState([]);
  const [originalServiceAreas, setOriginalServiceAreas] = useState([]);
  
  // State for editing
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  
  // State for pending changes
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  
  // State for sorting
  const [orderBy, setOrderBy] = useState('zip_code');
  const [order, setOrder] = useState('asc');
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  
  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  // Load data on component mount
  useEffect(() => {
    fetchServiceAreas();
  }, []);

  // Fetch service areas data
  const fetchServiceAreas = async () => {
    try {
      // Using apiFetch which automatically handles authorization headers and URL resolution
      const response = await apiFetch(`/api/service-areas`);
      
      if (!response.ok) {
        throw new Error(`Error fetching service areas: ${response.status}`);
      }
      
      const data = await response.json();
      setServiceAreas(data);
      setOriginalServiceAreas(JSON.parse(JSON.stringify(data))); // Deep copy for comparison
    } catch (error) {
      console.error('Error fetching service areas:', error);
      showAlert('Failed to load service area data', 'error');
    }
  };

  // Handle click on cell to edit
  const handleCellClick = (id, field, value) => {
    // Ignore clicks on ID and action cells
    if (field === 'id' || field === 'actions') {
      return;
    }
    
    setEditingCell({ id, field });
    setEditingValue(value || '');
  };

  // Handle change in editing value
  const handleEditingChange = (e) => {
    setEditingValue(e.target.value);
  };

  // Save edited cell
  const handleSaveCell = () => {
    if (!editingCell) return;

    const { id, field } = editingCell;
    
    // Update local state
    const updatedAreas = serviceAreas.map(area => {
      if (area.id === id) {
        return { ...area, [field]: editingValue };
      }
      return area;
    });
    
    setServiceAreas(updatedAreas);
    setEditingCell(null);
    setHasPendingChanges(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  // Handle key events in editing field
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveCell();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    } else if (e.key === 'Tab') {
      // Save current cell on tab
      e.preventDefault();
      handleSaveCell();
      
      // Find the next cell to edit
      const { id, field } = editingCell;
      const currentAreaIndex = serviceAreas.findIndex(area => area.id === id);
      const currentFieldIndex = columns.findIndex(col => col.id === field);
      
      // Calculate next field and row
      let nextFieldIndex = currentFieldIndex + 1;
      let nextRowIndex = currentAreaIndex;
      
      // If at the end of columns, move to next row
      if (nextFieldIndex >= columns.length) {
        nextFieldIndex = 0;
        nextRowIndex += 1;
      }
      
      // If at the end of rows, wrap to first row
      if (nextRowIndex >= serviceAreas.length) {
        nextRowIndex = 0;
      }
      
      // Move to next cell if we have valid indices
      if (nextRowIndex >= 0 && nextRowIndex < serviceAreas.length) {
        const nextArea = serviceAreas[nextRowIndex];
        const nextField = columns[nextFieldIndex].id;
        
        // Focus the next cell
        setTimeout(() => {
          handleCellClick(nextArea.id, nextField, nextArea[nextField] || '');
        }, 10);
      }
    }
  };
  
  // Handle focus out (blur) event to save the cell
  const handleBlur = (e) => {
    // Only save if we're not moving to another input within the component
    // This allows the Tab key to work properly
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !relatedTarget.classList.contains('cell-edit-input')) {
      handleSaveCell();
    }
  };

  // Save all changes
  const saveAllChanges = async () => {
    let success = true;
    let errorMessage = '';
    
    // Compare current state with original to find changed records
    for (const currentArea of serviceAreas) {
      const originalArea = originalServiceAreas.find(area => area.id === currentArea.id);
      
      // If this is a new record (no ID), add it
      if (!currentArea.id) {
        try {
          // Validate required fields
          if (!currentArea.zip_code || !currentArea.county) {
            showAlert('Zip code and county are required fields', 'error');
            return;
          }
          
          await addServiceArea(currentArea);
        } catch (error) {
          success = false;
          errorMessage = `Failed to add record: ${error.message}`;
          break;
        }
        continue;
      }
      
      // Check if record has changed
      if (JSON.stringify(currentArea) !== JSON.stringify(originalArea)) {
        try {
          // Validate required fields
          if (!currentArea.zip_code || !currentArea.county) {
            showAlert('Zip code and county are required fields', 'error');
            return;
          }
          
          await updateServiceArea(currentArea);
        } catch (error) {
          success = false;
          errorMessage = `Failed to update record ID ${currentArea.id}: ${error.message}`;
          break;
        }
      }
    }
    
    // Check for records that were in the original data but not in the current state (deleted)
    for (const originalArea of originalServiceAreas) {
      if (!serviceAreas.some(area => area.id === originalArea.id)) {
        try {
          await deleteServiceArea(originalArea.id);
        } catch (error) {
          success = false;
          errorMessage = `Failed to delete record ID ${originalArea.id}: ${error.message}`;
          break;
        }
      }
    }
    
    if (success) {
      // Refresh data and reset state
      fetchServiceAreas();
      setHasPendingChanges(false);
      showAlert('All changes saved successfully', 'success');
    } else {
      showAlert(errorMessage, 'error');
    }
  };

  // Add service area
  const addServiceArea = async (area) => {
    // Using apiFetch which automatically handles authorization headers and URL resolution
    const response = await apiFetch(`/api/service-areas`, {
      method: 'POST',
      body: JSON.stringify(area)
    });
    
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    
    return await response.json();
  };

  // Update service area
  const updateServiceArea = async (area) => {
    // Using apiFetch which automatically handles authorization headers and URL resolution
    const response = await apiFetch(`/api/service-areas/${area.id}`, {
      method: 'PUT',
      body: JSON.stringify(area)
    });
    
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    
    return await response.json();
  };

  // Delete service area
  const deleteServiceArea = async (id) => {
    // Using apiFetch which automatically handles authorization headers and URL resolution
    const response = await apiFetch(`/api/service-areas/${id}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (id) => {
    setRecordToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  // Handle delete
  const handleDelete = () => {
    // Remove record from local state
    setServiceAreas(serviceAreas.filter(area => area.id !== recordToDelete));
    setHasPendingChanges(true);
    closeDeleteDialog();
  };

  // Add new row
  const handleAddRow = () => {
    // Add empty record to the top of the list
    const newRow = {
      id: null, // null ID indicates a new record
      zip_code: '',
      county: '',
      territory: '',
      service_area: '',
      notes: ''
    };
    
    setServiceAreas([newRow, ...serviceAreas]);
    setHasPendingChanges(true);
    
    // Start editing the zip code field of the new row
    setTimeout(() => {
      setEditingCell({ id: null, field: 'zip_code' });
      setEditingValue('');
    }, 100);
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
    // Sort the data
    const sortedData = [...serviceAreas].sort((a, b) => {
      // Handle empty values
      const valueA = a[property] || '';
      const valueB = b[property] || '';
      
      // Sort by string values
      if (order === 'asc') {
        return valueA.localeCompare(valueB);
      } else {
        return valueB.localeCompare(valueA);
      }
    });
    
    setServiceAreas(sortedData);
  };

  // Show alert
  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Close alert
  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertOpen(false);
  };

  // Get cell content (either editable field or text)
  const getCellContent = (area, field) => {
    const value = area[field] || '';
    const isEditing = editingCell && editingCell.id === area.id && editingCell.field === field;
    
    if (isEditing) {
      return (
        <TextField
          fullWidth
          variant="standard"
          size="small"
          value={editingValue}
          onChange={handleEditingChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          autoFocus
          className="cell-edit-input"
          sx={{ m: 0 }}
          InputProps={{
            disableUnderline: false,
          }}
        />
      );
    }
    
    return (
      <div
        onClick={() => handleCellClick(area.id, field, value)}
        style={{ 
          width: '100%', 
          height: '100%', 
          padding: '8px',
          ...(field !== 'id' && styles.editableCell) 
        }}
      >
        {value || '-'}
      </div>
    );
  };

  // Define columns
  const columns = [
    { id: 'zip_code', label: 'Zip Code', sortable: true, required: true },
    { id: 'county', label: 'County', sortable: true, required: true },
    { id: 'territory', label: 'Territory', sortable: true },
    { id: 'service_area', label: 'Service Area', sortable: true },
    { id: 'notes', label: 'Notes', sortable: true },
  ];

  return (
    <Container>
      <Box sx={styles.listViewHeader}>
        <Typography variant="h5">
          Service Area Management
        </Typography>
        <Box>
          {hasPendingChanges && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={saveAllChanges}
              sx={{ mr: 1 }}
            >
              Save Changes
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
          >
            Add Row
          </Button>
        </Box>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2 }}>
        Click on any cell to edit its value. Click column headers to sort. Hover over a row to see delete option.
      </Typography>
      
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell 
                  key={column.id}
                  sx={styles.headerCell}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                      {column.required && ' *'}
                    </TableSortLabel>
                  ) : (
                    <span>
                      {column.label}
                      {column.required && ' *'}
                    </span>
                  )}
                </TableCell>
              ))}
              <TableCell sx={styles.headerCell} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {serviceAreas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No service areas found. Click "Add Row" to create one.
                </TableCell>
              </TableRow>
            ) : (
              serviceAreas.map((area) => (
                <TableRow 
                  key={area.id || 'new-row'}
                  hover
                >
                  {columns.map(column => (
                    <TableCell 
                      key={`${area.id || 'new'}-${column.id}`}
                      sx={editingCell && editingCell.id === area.id && editingCell.field === column.id 
                        ? styles.editingCell 
                        : undefined}
                      padding="none"
                    >
                      {getCellContent(area, column.id)}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={styles.actionCell}>
                    <IconButton
                      color="error"
                      onClick={() => area.id ? openDeleteDialog(area.id) : setServiceAreas(serviceAreas.filter(a => a !== area))}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddRow}
        sx={styles.newRowButton}
        fullWidth
      >
        Add New Row
      </Button>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this service area? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert Snackbar */}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ServiceAreaManagement;